import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { useOutsideClick } from 'hooks'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import IconButton from 'components/priority/IconButton/IconButton'
import Icon from 'components/priority/Icon/Icon'
import type { Locale } from '@messenger/types'
import {
  LOCALE_VALUES,
  LOCALE_MAP,
  LOCALE_TRANSLATED_DISPLAY_NAMES,
} from '@messenger/consts'

type LanguageSelectorProps = {
  lockedWhite?: boolean
  whiteIcon?: boolean
}

const LanguageSelector = (props: LanguageSelectorProps) => {
  const { i18n } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null!)
  const [hoveredLanguage, setHoveredLanguage] = useState<Locale | null>(null)
  const { whiteIcon = false } = props

  useOutsideClick(ref, open, () => setOpen(false), 'mousedown')

  const handleLanguageChange = (locale: Locale) => {
    const mappedLocaleValue = LOCALE_MAP[locale]
    i18n.changeLanguage(mappedLocaleValue)
    localStorage.setItem('language', mappedLocaleValue)

    // Update lang query param using React Router
    const searchParams = new URLSearchParams(location.search)
    searchParams.set('lang', mappedLocaleValue)
    history.replace(`${location.pathname}?${searchParams.toString()}`)

    setOpen(false)
  }

  return (
    <Flex position="relative" ref={ref} padding="0 16px">
      <IconButton
        aria-label="Select language"
        icon="languageToggle"
        onClick={() => setOpen((v) => !v)}
        {...(whiteIcon ? { fill: 'lockedWhite' } : {})}
      />
      {open && (
        <Flex
          column
          position="absolute"
          top="100%"
          left="-125px"
          border="1px solid #eee"
          radius="6px"
          shadow="0 2px 8px rgba(0,0,0,0.08)"
          width="200px"
          margin="4px 0 0 0"
          background="FloatingMenu"
          zindex="9999"
        >
          {LOCALE_VALUES.map((locale) => {
            const mappedLocaleValue = LOCALE_MAP[locale]
            const isSelected = i18n.language === mappedLocaleValue
            const isHovered = hoveredLanguage === locale
            const displayName = LOCALE_TRANSLATED_DISPLAY_NAMES[locale]
            return (
              <Flex
                key={locale}
                padding="16px 24px"
                align="center"
                border="none !important"
                style={{
                  cursor: 'pointer',
                  background: isHovered ? '#797E83' : undefined,
                  transition: 'background 0.2s',
                }}
                onClick={() => handleLanguageChange(locale)}
                onMouseEnter={() => setHoveredLanguage(locale)}
                onMouseLeave={() => setHoveredLanguage(null)}
              >
                <Text size="18px" weight={isSelected ? 700 : 400}>
                  {displayName}
                </Text>
                {isSelected && (
                  <Icon
                    icon="check"
                    fill="#1A6CAB"
                    margin="0 0 0 12px"
                    width="18px"
                    height="18px"
                  />
                )}
              </Flex>
            )
          })}
        </Flex>
      )}
    </Flex>
  )
}

export default LanguageSelector
